import type { MessageResponse } from '@/domain/Api';
import { api } from '@/lib/api';
import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mutationKeys } from '../mutationKeys';
import { queryKeys } from '../queryKeys';
import type { PromoCode } from './promoQueries';

export interface CreatePromoCodeRequest {
  discount: number | string;
}

export interface CreatePromoCodeResponse {
  data: PromoCode;
}

export const createPromoCodeMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.promoCodes.create(),
    mutationFn: async (values: CreatePromoCodeRequest) => {
      const payload = {
        ...values,
        discount: Number(values.discount),
      };
      return await api
        .post('promo-codes', { json: payload })
        .json<CreatePromoCodeResponse>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.promoCodes.all(),
      });
      toast.success('Promo code generated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to generate promo code');
    },
  });
};

export const deletePromoCodeMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationFn: async (code: string) => {
      return await api.delete(`promo-codes/${code}`).json<MessageResponse>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.promoCodes.all(),
      });
      toast.success('Promo code deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete promo code');
    },
  });
};

export const validatePromoCodeMutation = mutationOptions({
  mutationFn: async (code: string) => {
    return await api.get(`promo-codes/${code}`).json<CreatePromoCodeResponse>();
  },
});

export interface UpdatePromoCodeRequest {
  promo_code: string;
  email: string;
}

export const updatePromoCodeMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.promoCodes.update(),
    mutationFn: async ({ promo_code, email }: UpdatePromoCodeRequest) => {
      return await api
        .put(`promo-codes/${promo_code}`, { json: { email } })
        .json<CreatePromoCodeResponse>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.promoCodes.all(),
      });
      toast.success('Recipient email updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update recipient email');
    },
  });
};

export interface SendPromoCodeRequest {
  promo_code: string;
  email: string;
}

export const sendPromoCodeMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.promoCodes.send(),
    mutationFn: async ({ promo_code, email }: SendPromoCodeRequest) => {
      return await api
        .post('promo-codes/send', { json: { promo_code, email } })
        .json<MessageResponse>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.promoCodes.all(),
      });
      toast.success('Promo code sent successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send promo code');
    },
  });
};
